import { Button, Checkbox, FileDrop, Icon, Input, Table, Tooltip } from '@scuf/common';
import React, { useEffect, useRef, useState } from 'react';
import './modalMensagemPortal.css';

interface ModalMensagemPortalProps {
    customDisplayName: string;
    setCustomDisplayName: (value: string) => void;
    t: (key: string) => string;
    onImageSelected: (file: File) => void;
    onSelectedSection: (sections: string) => void;
    onSelectedLanguage: (languages: string) => void;
}

const ModalMensagemPortal: React.FC<ModalMensagemPortalProps> = ({
    customDisplayName,
    setCustomDisplayName,
    t,
    onImageSelected,
    onSelectedSection,
    onSelectedLanguage
}) => {
    const [imagemAdicionada, setImagemAdicionada] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [validationError, setValidationError] = useState('');
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    //Controle de seções selecionadas
    const [homeMarcado, setHomeMarcado] = useState(false);
    const [repairCenterMarcado, setRepairCenterMarcado] = useState(false);
    const [sparePartsMarcado, setSparePartsMarcado] = useState(false);
    const [manufaturaMarcada, setManufaturaMarcada] = useState(false);
    const [hslMarcado, setHslMarcado] = useState(false);
    const [bslBrMarcado, setBslBrMarcado] = useState(false);

    const [portugues, setPortugues] = useState(false);
    const [espanhol, setEspanhol] = useState(false);
    const [ingles, setIngles] = useState(false);

    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);


    const handleFile = (files: File[]) => {
        const file = files[0];

        if (!file) return;

        const previewUrl = URL.createObjectURL(file);

        setSelectedImage(file);
        setImagePreviewUrl(previewUrl);

        onImageSelected(file);

        setImageLoading(true);

        setTimeout(() => {
            setImagemAdicionada(true);
            setImageLoading(false);
        }, 500);
    };

    const handleTrocarImagem = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
        }

        setSelectedImage(null);
        setImagePreviewUrl(null);
        setValidationError('');
        setImageLoading(false);
        setImagemAdicionada(false);
    };

    useEffect(() => {
        const selectedSections = [
            homeMarcado ? 'msg-home' : null,
            repairCenterMarcado ? 'msg-repair' : null,
            sparePartsMarcado ? 'msg-spare' : null,
            manufaturaMarcada ? 'msg-manufatura' : null,
            hslMarcado ? 'msg-hsl' : null,
            bslBrMarcado ? 'msg-bslbr' : null
        ]
            .filter((section): section is string => section !== null)
            .join(',');
        onSelectedSection(selectedSections);
    }, [
        homeMarcado,
        repairCenterMarcado,
        sparePartsMarcado,
        manufaturaMarcada,
        hslMarcado,
        bslBrMarcado,
        onSelectedSection,
    ]);

    useEffect(() => {
        const selectedLanguages = [
            portugues ? 'PT' : null,
            ingles ? 'EN' : null,
            espanhol ? 'ES' : null,
        ]
            .filter((language): language is string => language !== null)
            .join(',');

        onSelectedLanguage(selectedLanguages);
    }, [
        portugues,
        ingles,
        espanhol,
        onSelectedLanguage,
    ]);

    return (
        <div className="modal-mensagem-portal">
            {imageLoading ? (
                <div
                    className="image-loading-container"
                    role="status"
                    aria-live="polite"
                    aria-label="Processando imagem"
                >
                    <div
                        className="circular-loader"
                        aria-hidden="true"
                    />

                    <p className="image-loading-text">
                        Processando imagem...
                    </p>
                </div>
            ) : !imagemAdicionada ? (
                <div className="upload-step">
                    <div className="file-drop-container">
                        <FileDrop
                            label={t("manual.AddImage")}
                            acceptedTypes={['image/*']}
                            placeholder={t("manual.Drop")}
                            onAccepted={handleFile}
                        />
                    </div>
                </div>
            ) : (
                <div className="message-configuration">
                    <div className="message-configuration__left">
                        {imagePreviewUrl && selectedImage && (
                            <div className="selected-image-preview">
                                <div className="selected-image-preview__image-container">
                                    <img
                                        className="selected-image-preview__image"
                                        src={imagePreviewUrl}
                                        alt="Pré-visualização da mensagem do portal"
                                    />
                                </div>

                                <div className="selected-image-preview__details">
                                    <span
                                        className="selected-image-preview__name"
                                        title={selectedImage.name}
                                    >
                                        {selectedImage.name}
                                    </span>

                                    <span className="selected-image-preview__size">
                                        {(selectedImage.size / 1024).toFixed(1)} KB
                                    </span>

                                    <Button
                                        type="secondary"
                                        icon="delete"
                                        content={t("manual.ImageDelete")}
                                        onClick={handleTrocarImagem}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="message-configuration__right">
                        <div className="message-name-container">
                            <Input
                                style={{ width: '100%' }}
                                placeholder={t("manual.InfoMessage")}
                                value={customDisplayName}
                                onChange={(value) => {
                                    setCustomDisplayName(value);
                                    setValidationError('');
                                }}
                            />
                        </div>
                        <div>
                            <div className="message-configuration__options">
                                <Table className="configuration-table sections-table">
                                    <Table.Header>
                                        <Table.HeaderCell />
                                        <Table.HeaderCell content={t("manual.Sections")} />
                                    </Table.Header>

                                    <Table.Body>
                                        <Table.Row key="home">
                                            <Table.Cell>
                                                <Checkbox
                                                    checked={homeMarcado}
                                                    onChange={() =>
                                                        setHomeMarcado(
                                                            (current) => !current
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>
                                                {t('manual.homepage')}{' '}
                                                <Tooltip
                                                    element={
                                                        <Icon
                                                            name="badge-info"
                                                            root="common"
                                                            size="small"
                                                        />
                                                    }
                                                    content={t('manual.msg_Home')}
                                                    hideOnScroll
                                                    size="tiny"
                                                    position="right center"
                                                />
                                            </Table.Cell>
                                        </Table.Row>

                                        <Table.Row key="repair">
                                            <Table.Cell>
                                                <Checkbox
                                                    checked={repairCenterMarcado}
                                                    onChange={() =>
                                                        setRepairCenterMarcado(
                                                            (current) => !current
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>
                                                {t('manual.RepairCenter')}{' '}
                                                <Tooltip
                                                    element={
                                                        <Icon
                                                            name="badge-info"
                                                            root="common"
                                                            size="small"
                                                        />
                                                    }
                                                    content={t('manual.msg_Repair')}
                                                    hideOnScroll
                                                    size="tiny"
                                                    position="right center"
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                        <Table.Row key="spare">
                                            <Table.Cell>
                                                <Checkbox
                                                    checked={sparePartsMarcado}
                                                    onChange={() =>
                                                        setSparePartsMarcado(
                                                            (current) => !current
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>
                                                {t('manual.SpareParts')}{' '}
                                                <Tooltip
                                                    element={
                                                        <Icon
                                                            name="badge-info"
                                                            root="common"
                                                            size="small"
                                                        />
                                                    }
                                                    content={t('manual.msg_Spare')}
                                                    hideOnScroll
                                                    size="tiny"
                                                    position="right center"
                                                />
                                            </Table.Cell>
                                        </Table.Row>

                                        <Table.Row key="manufatura">
                                            <Table.Cell>
                                                <Checkbox
                                                    checked={manufaturaMarcada}
                                                    onChange={() =>
                                                        setManufaturaMarcada(
                                                            (current) => !current
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>
                                                {t('manual.manufacturing')}{' '}
                                                <Tooltip
                                                    element={
                                                        <Icon
                                                            name="badge-info"
                                                            root="common"
                                                            size="small"
                                                        />
                                                    }
                                                    content={t('manual.msg_Manufatura')}
                                                    hideOnScroll
                                                    size="tiny"
                                                    position="right center"
                                                />
                                            </Table.Cell>
                                        </Table.Row>

                                        <Table.Row key="hsl">
                                            <Table.Cell>
                                                <Checkbox
                                                    checked={hslMarcado}
                                                    onChange={() =>
                                                        setHslMarcado(
                                                            (current) => !current
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>
                                                {t('manual.HSL')}{' '}
                                                <Tooltip
                                                    element={
                                                        <Icon
                                                            name="badge-info"
                                                            root="common"
                                                            size="small"
                                                        />
                                                    }
                                                    content={t('manual.msg_HSL')}
                                                    hideOnScroll
                                                    size="tiny"
                                                    position="right center"
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                        <Table.Row key="bslbr">
                                            <Table.Cell>
                                                <Checkbox
                                                    checked={bslBrMarcado}
                                                    onChange={() =>
                                                        setBslBrMarcado(
                                                            (current) => !current
                                                        )
                                                    }
                                                />
                                            </Table.Cell>
                                            <Table.Cell>
                                                {t('manual.BSLBR')}{' '}
                                                <Tooltip
                                                    element={
                                                        <Icon
                                                            name="badge-info"
                                                            root="common"
                                                            size="small"
                                                        />
                                                    }
                                                    content={t('manual.msg_BSLBR')}
                                                    hideOnScroll
                                                    size="tiny"
                                                    position="right center"
                                                />
                                            </Table.Cell>
                                        </Table.Row>
                                    </Table.Body>
                                </Table>

                                <Table className="configuration-table languages-table">
                                    <Table.Header>
                                        <Table.HeaderCell
                                            content={<div className="language-header">
                                                {t("manual.Language")}
                                                <Tooltip
                                                    content={t("manual.InfoLanguage")}
                                                    position="right center"
                                                    element={<Icon name="badge-info" color="white" />}
                                                />
                                            </div>}
                                        />
                                    </Table.Header>

                                    <Table.Body>
                                        <Table.Row key="languages">
                                            <Table.Cell>
                                                <div className="language-options">
                                                    <div className="language-option">
                                                        <Tooltip
                                                            content={t("manual.PortugueseInfo")}
                                                            position="top center"
                                                            element={
                                                                <Checkbox
                                                                    checked={portugues}
                                                                    onChange={() =>
                                                                        setPortugues((current) => !current)
                                                                    }
                                                                />
                                                            }
                                                        />
                                                        <span>{t("manual.Portuguese")}</span>
                                                        <img src="/assets/icons/pt-br.png" alt="PT/BR" width={25} />
                                                    </div>
                                                    <div className="language-option">
                                                        <Tooltip
                                                            content={t("manual.EnglishInfo")}
                                                            position="top center"
                                                            element={
                                                                <Checkbox
                                                                    checked={ingles}
                                                                    onChange={() =>
                                                                        setIngles((current) => !current)
                                                                    }
                                                                />}
                                                        />
                                                        <span>{t("manual.English")}</span>
                                                        <img src="/assets/icons/en-us.png" alt="EN/US" width={25} />
                                                    </div>

                                                    <div className="language-option">
                                                        <Tooltip
                                                            content={t("manual.SpanishInfo")}
                                                            position="top center"
                                                            element={
                                                                <Checkbox
                                                                    checked={espanhol}
                                                                    onChange={() =>
                                                                        setEspanhol((current) => !current)
                                                                    }
                                                                />
                                                            }
                                                        />
                                                        <span>{t("manual.Spanish")}</span>
                                                        <img src="/assets/icons/es-mx.png" alt="ES" width={25} />
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    </Table.Body>
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            )
            }
        </div >
    );
}
export default ModalMensagemPortal;